import { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import useAuthStore from '@entities/auth/model/useAuthStore';
import useAlertStore from '@entities/ui/model/useAlertStore';
import useUnsavedLeaveStore, { UNSAVED_SCOPE } from '@shared/model/useUnsavedLeaveStore';
import { useQueries, useQuery } from '@tanstack/react-query';
import {
  useDocFormListQuery,
  useDocFormListHtmlQuery,
  useDocFormListAdminQuery,
  useInsertFormMutation,
  useUpdateFormMutation,
  useDeleteFormMutation,
} from '@features/form-selection/hooks';
import {
  getFormReceiverList,
  getFormExecutorList,
  getApprovalLineGuidesByDoc,
  getDocFormCheckEmp,
} from '@entities/form/api/form';
import AdminLayout from '@shared/components/organisms/AdminLayout/AdminLayout';
import SplitPageLayout from '@shared/components/organisms/SplitPageLayout/SplitPageLayout';
import Button from '@shared/components/atoms/Button/Button';
import Confirm from '@shared/components/atoms/Confirm/Confirm';
import { useConfirm } from '@shared/hooks';
import CKEditorWrapper from '@features/document-write/components/CKEditorWrapper/CKEditorWrapper';
import FormRecipientModal from './components/FormRecipientModal';
import { useEmployeeInfosQuery } from '@features/employee/hooks';

import styles from './FormAddEditPage.module.scss';

/**
 * FormAddEditPage
 * - 양식 추가/편집 페이지
 * - 스플릿 화면으로 양식 정보 입력 및 CKEditor로 양식 내용 작성
 */
export default function FormAddEditPage() {
  const navigate = useNavigate();
  const { docId } = useParams();
  const location = useLocation();
  const companyCode = useAuthStore((state) => state.companyCode);
  const showAlert = useAlertStore((state) => state.showAlert);
  const isEditMode = !!docId;
  const routeFormInfo = location.state?.formInfo || null;
  const shouldFetchFormInfo = isEditMode && !!docId && !routeFormInfo;

  // 양식 데이터 상태
  const [formData, setFormData] = useState({
    doc: null,
    category: '',
    title: '',
    form: '',
    required_docs: '',
    form_data: '',
    senderList: [],
    executorList: [],
    checkEmpList: [],
    // 결재라인 설정 (배열로 관리)
    approvalLineList: [
      {
        guideTitle: '',
        reviewerDept: '',
        recipient: '',
        approvalLineOptions: {
          teamLeader: false, // 1차
          departmentHead: false, // 2차
          agreement: false, // 3차
          agreementRequired: false, // 합의(경영기획본부장)
          ceo: false, // 대표이사
        },
      },
    ],
  });

  // 모달 상태
  const [recipientModalOpen, setRecipientModalOpen] = useState(false);
  const [recipientModalType, setRecipientModalType] = useState(null); // 'sender' | 'executor' | 'reviewer'
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // 저장 없이 이동 시 확인용 (목록/LNB/로그아웃)
  const [hasUserEdited, setHasUserEdited] = useState(false);
  // 참조자/시행자 모달에서 사용자 저장 시 API 동기화 useEffect가 덮어쓰지 않도록 플래그
  const hasSenderListBeenManuallyEdited = useRef(false);
  const hasExecutorListBeenManuallyEdited = useRef(false);
  const { confirmState, showConfirm, closeConfirm } = useConfirm();
  const pendingLeave = useUnsavedLeaveStore((state) => state.pendingLeave);
  const confirmLeave = useUnsavedLeaveStore((state) => state.confirmLeave);
  const cancelLeave = useUnsavedLeaveStore((state) => state.cancelLeave);
  const setUnsaved = useUnsavedLeaveStore((state) => state.setUnsaved);
  const clearUnsaved = useUnsavedLeaveStore((state) => state.clearUnsaved);

  // 양식 목록 조회 (문서분류 옵션용)
  const { data: docFormList = [] } = useDocFormListAdminQuery(companyCode);

  // 양식 데이터 조회 (편집 모드) - getDocFormList 사용 (form_data 포함)
  const { data: allFormListWithData = [], isLoading: isLoadingFormList } = useDocFormListQuery(
    companyCode,
    {
      enabled: shouldFetchFormInfo,
    },
  );

  // 양식 데이터 조회 (편집 모드) - getDocFormListHtml 사용 (category 포함)
  const { data: allFormListWithCategory = [] } = useDocFormListHtmlQuery(companyCode, {
    enabled: shouldFetchFormInfo,
  });

  // 편집 모드: 전체 목록에서 docId에 해당하는 양식 찾기 및 데이터 병합
  const formDataFromApi = useMemo(
    function () {
      if (!shouldFetchFormInfo || !allFormListWithData.length) {
        return null;
      }
      // getDocFormList에서 form_data 포함 데이터 찾기
      const formWithData = allFormListWithData.find(function (form) {
        return form.doc === Number(docId);
      });

      // getDocFormListHtml에서 category 포함 데이터 찾기
      const formWithCategory = allFormListWithCategory.find(function (form) {
        return form.doc === Number(docId);
      });

      if (!formWithData) {
        return null;
      }

      // 두 데이터 병합
      return {
        ...formWithData,
        category: formWithCategory?.category || '',
      };
    },
    [shouldFetchFormInfo, docId, allFormListWithData, allFormListWithCategory],
  );

  const isLoadingFormData = isLoadingFormList;

  // 문서 번호 결정 (routeFormInfo가 있으면 우선 사용, 없으면 URL 파라미터 사용)
  const currentDocId = useMemo(
    function () {
      return routeFormInfo?.doc || (docId ? Number(docId) : null);
    },
    [routeFormInfo, docId],
  );

  // 참조자 목록 조회 (편집 모드)
  const { data: receiverListDataRaw } = useQuery({
    queryKey: ['formReceiverList', currentDocId, companyCode],
    queryFn: function () {
      return getFormReceiverList(currentDocId, companyCode);
    },
    enabled: isEditMode && !!currentDocId && !!companyCode,
    select: function (data) {
      return data?.resultData?.receiverUserList || [];
    },
  });

  // 시행자 목록 조회 (편집 모드)
  const { data: executorListDataRaw } = useQuery({
    queryKey: ['formExecutorList', currentDocId, companyCode],
    queryFn: function () {
      return getFormExecutorList(currentDocId, companyCode);
    },
    enabled: isEditMode && !!currentDocId && !!companyCode,
    select: function (data) {
      return data?.resultData?.formExecutorList || [];
    },
  });

  // 참조자 목록 메모이제이션 (의존성 배열 문제 해결)
  const receiverListData = useMemo(
    function () {
      return receiverListDataRaw || [];
    },
    [receiverListDataRaw],
  );

  // 시행자 목록 메모이제이션 (의존성 배열 문제 해결)
  const executorListData = useMemo(
    function () {
      return executorListDataRaw || [];
    },
    [executorListDataRaw],
  );

  // 결재라인 가이드라인 조회 (편집 모드) - 여러 개 조회
  const { data: approvalLineGuidesData } = useQuery({
    queryKey: ['approvalLineGuidesByDoc', currentDocId, companyCode],
    queryFn: function () {
      return getApprovalLineGuidesByDoc(currentDocId, companyCode);
    },
    enabled: isEditMode && !!currentDocId && !!companyCode,
    select: function (data) {
      // 가이드라인 목록이 없는 경우 빈 배열 반환
      const resultData = data?.resultData;
      if (!resultData || !Array.isArray(resultData) || resultData.length === 0) {
        return [];
      }
      return resultData;
    },
  });

  // 검토자 정보 조회 (편집 모드)
  const { data: checkEmpData } = useQuery({
    queryKey: ['formCheckEmp', currentDocId, companyCode],
    queryFn: function () {
      return getDocFormCheckEmp(currentDocId);
    },
    enabled: isEditMode && !!currentDocId && !!companyCode,
    select: function (data) {
      return data?.resultData || {};
    },
  });

  // 검토자 enumber 배열 생성 (check1~check4 중 유효한 값만)
  const reviewerEnumbers = useMemo(
    function () {
      if (!checkEmpData) {
        return [];
      }
      const enumbers = [];
      if (checkEmpData.check1 && checkEmpData.check1 > 0) {
        enumbers.push({ enumber: checkEmpData.check1 });
      }
      if (checkEmpData.check2 && checkEmpData.check2 > 0) {
        enumbers.push({ enumber: checkEmpData.check2 });
      }
      if (checkEmpData.check3 && checkEmpData.check3 > 0) {
        enumbers.push({ enumber: checkEmpData.check3 });
      }
      if (checkEmpData.check4 && checkEmpData.check4 > 0) {
        enumbers.push({ enumber: checkEmpData.check4 });
      }
      return enumbers;
    },
    [checkEmpData],
  );

  // 검토자 직원 정보 병렬 조회
  const reviewerEmployeeQueries = useEmployeeInfosQuery(reviewerEnumbers, {
    enabled: isEditMode && reviewerEnumbers.length > 0,
  });

  // 검토자 직원 쿼리 결과를 안정적인 의존성으로 변환
  const reviewerEmployeeData = useMemo(
    function () {
      if (!reviewerEmployeeQueries || reviewerEmployeeQueries.length === 0) {
        return [];
      }
      // 각 쿼리의 데이터만 추출하여 안정적인 배열 생성
      return reviewerEmployeeQueries.map(function (query) {
        return {
          isSuccess: query.isSuccess,
          data: query.data?.resultData || null,
        };
      });
    },
    [
      reviewerEmployeeQueries
        .map(function (q, i) {
          return `${i}-${q.isSuccess}-${q.data?.resultData?.enumber || 'null'}-${q.data?.resultData?.name || 'null'}`;
        })
        .join('|'),
    ],
  );

  // Mutation 훅
  const insertFormMutation = useInsertFormMutation({
    onSuccess: function () {
      clearUnsaved();
      // 캐시는 훅 내부에서 invalidate(refetchType: 'none') 처리됨 → 목록 페이지 진입 시 자동 refetch
      showAlert('저장되었습니다.', function () {
        navigate('/admin/forms/document');
      });
    },
    onError: function (error) {
      console.error('양식 추가 오류:', error);
      // 백엔드에서 반환한 에러 메시지가 있으면 사용, 없으면 기본 메시지
      const errorMessage =
        error?.response?.data?.error_msg ||
        error?.message ||
        error?.response?.data?.message ||
        '양식 추가 중 오류가 발생했습니다.';
      // 에러 발생 시 알럿만 표시하고 페이지 이동하지 않음 (작성 상태 유지)
      showAlert(errorMessage);
    },
  });

  const updateFormMutation = useUpdateFormMutation({
    onSuccess: function () {
      clearUnsaved();
      showAlert('수정되었습니다.', function () {
        navigate('/admin/forms/document');
      });
    },
    onError: function (error) {
      console.error('양식 수정 오류:', error);
      // 백엔드에서 반환한 에러 메시지가 있으면 사용, 없으면 기본 메시지
      const errorMessage =
        error?.response?.data?.error_msg ||
        error?.message ||
        error?.response?.data?.message ||
        '양식 수정 중 오류가 발생했습니다.';
      // 에러 발생 시 알럿만 표시하고 페이지 이동하지 않음 (작성 상태 유지)
      showAlert(errorMessage);
    },
  });

  const deleteFormMutation = useDeleteFormMutation({
    onSuccess: function () {
      clearUnsaved();
      showAlert('양식이 삭제되었습니다.', function () {
        navigate('/admin/forms/document');
      });
    },
    onError: function (error) {
      console.error('양식 삭제 오류:', error);
      showAlert('양식 삭제 중 오류가 발생했습니다.');
    },
  });

  // 저장 없이 이동 시 확인: 사용자가 편집한 경우에만 store 등록 (LNB/로그아웃용)
  useEffect(
    function () {
      if (hasUserEdited) {
        setUnsaved(UNSAVED_SCOPE.FORM_ADD_EDIT, function getResetAndProceed() {
          return function runThenNext(next) {
            clearUnsaved();
            if (typeof next === 'function') next();
          };
        });
      } else {
        clearUnsaved();
      }
      return function cleanup() {
        clearUnsaved();
      };
    },
    [hasUserEdited, setUnsaved, clearUnsaved],
  );

  // 문서분류 옵션 생성
  const categoryOptions = useMemo(
    function () {
      return ['전체', ...new Set(docFormList.map((item) => item.category).filter(Boolean))];
    },
    [docFormList],
  );

  // 편집 모드: API에서 데이터 로드
  // sourceFormInfo를 useMemo로 메모이제이션하여 의존성 배열 문제 해결
  const sourceFormInfo = useMemo(
    function () {
      return routeFormInfo || formDataFromApi;
    },
    [routeFormInfo, formDataFromApi],
  );

  // 빈 배열 상수 (참조 안정성을 위해)
  const EMPTY_ARRAY = useMemo(function () {
    return [];
  }, []);

  // 참조자/시행자 목록 변환을 useMemo로 메모이제이션
  const convertedSenderList = useMemo(
    function () {
      if (!isEditMode || !receiverListData || receiverListData.length === 0) {
        return EMPTY_ARRAY;
      }
      return receiverListData.map(function (item) {
        return {
          enumber: item.enumber || 0,
          name: item.name || '',
          position: item.position || '',
          team: item.team || '',
        };
      });
    },
    [isEditMode, receiverListData, EMPTY_ARRAY],
  );

  const convertedExecutorList = useMemo(
    function () {
      if (!isEditMode || !executorListData || executorListData.length === 0) {
        return EMPTY_ARRAY;
      }
      return executorListData.map(function (item) {
        return {
          enumber: item.enumber || 0,
          name: item.name || '',
          position: item.position || '',
          team: item.team || '',
        };
      });
    },
    [isEditMode, executorListData, EMPTY_ARRAY],
  );

  // 검토자 목록 변환 (check1~check4에서 직원 정보 조회 결과 반영)
  const convertedCheckEmpList = useMemo(
    function () {
      if (!isEditMode || !reviewerEnumbers.length || !reviewerEmployeeData.length) {
        return EMPTY_ARRAY;
      }

      const checkEmpList = [];
      reviewerEmployeeData.forEach(function (queryResult, index) {
        if (queryResult.isSuccess && queryResult.data) {
          const employeeData = queryResult.data;
          const enumber = reviewerEnumbers[index]?.enumber;
          if (enumber) {
            checkEmpList.push({
              enumber: enumber,
              name: employeeData.name || '',
              position: employeeData.position || '',
              team: employeeData.team || '',
            });
          }
        }
      });

      return checkEmpList.length > 0 ? checkEmpList : EMPTY_ARRAY;
    },
    [isEditMode, reviewerEnumbers, reviewerEmployeeData, EMPTY_ARRAY],
  );

  // formData 초기화를 위한 안정적인 키 생성 (sourceFormInfo의 doc만 사용)
  const sourceDocId = useMemo(
    function () {
      return sourceFormInfo?.doc || null;
    },
    [sourceFormInfo?.doc],
  );

  // 기본 양식 데이터 초기화 (수신자, 시행자, 검토자 제외 - 별도 처리)
  useEffect(
    function () {
      // 편집 모드이고 sourceFormInfo가 있고, 아직 초기화되지 않았을 때만 실행
      if (isEditMode && sourceFormInfo && sourceDocId && formData.doc !== sourceDocId) {
        setFormData(function (prev) {
          return {
            ...prev,
            doc: sourceFormInfo.doc,
            category: sourceFormInfo.category || '',
            title: sourceFormInfo.title || '',
            form: sourceFormInfo.form || '',
            required_docs: sourceFormInfo.requiredDocs || sourceFormInfo.required_docs || '',
            form_data:
              sourceFormInfo.formDataHtml ||
              sourceFormInfo.form_data_html ||
              sourceFormInfo.form_data ||
              '',
            // 결재라인 정보 초기화 (가이드라인 조회 전까지 기본값)
            approvalLineList: [
              {
                guideTitle: '',
                reviewerDept: '',
                recipient: '',
                approvalLineOptions: {
                  teamLeader: false,
                  departmentHead: false,
                  agreement: false,
                  agreementRequired: false,
                  ceo: false,
                },
              },
            ],
          };
        });
      }
    },
    [isEditMode, sourceFormInfo, sourceDocId, formData.doc],
  );

  // 문서 변경 시 참조자/시행자 편집 플래그 초기화
  useEffect(
    function () {
      hasSenderListBeenManuallyEdited.current = false;
      hasExecutorListBeenManuallyEdited.current = false;
    },
    [sourceDocId],
  );

  // 수신자 정보 업데이트 (API에서 로드된 데이터만, 사용자가 수정하지 않은 경우에만)
  const senderListKey = useMemo(
    function () {
      return convertedSenderList
        .map(function (item) {
          return item.enumber;
        })
        .join(',');
    },
    [convertedSenderList],
  );

  const currentSenderListKey = useMemo(
    function () {
      return formData.senderList
        .map(function (item) {
          return item.enumber;
        })
        .join(',');
    },
    [formData.senderList],
  );

  useEffect(
    function () {
      // 편집 모드이고, API에서 받아온 데이터가 있고, 사용자가 아직 수정하지 않은 경우에만 업데이트
      if (
        isEditMode &&
        sourceDocId &&
        formData.doc === sourceDocId &&
        senderListKey &&
        senderListKey !== currentSenderListKey &&
        formData.senderList.length === 0 &&
        !hasSenderListBeenManuallyEdited.current
      ) {
        setFormData(function (prev) {
          return {
            ...prev,
            senderList: convertedSenderList,
          };
        });
      }
    },
    [
      isEditMode,
      sourceDocId,
      formData.doc,
      senderListKey,
      currentSenderListKey,
      convertedSenderList,
      formData.senderList.length,
    ],
  );

  // 시행자 정보 업데이트 (API에서 로드된 데이터만, 사용자가 수정하지 않은 경우에만)
  const executorListKey = useMemo(
    function () {
      return convertedExecutorList
        .map(function (item) {
          return item.enumber;
        })
        .join(',');
    },
    [convertedExecutorList],
  );

  const currentExecutorListKey = useMemo(
    function () {
      return formData.executorList
        .map(function (item) {
          return item.enumber;
        })
        .join(',');
    },
    [formData.executorList],
  );

  useEffect(
    function () {
      // 편집 모드이고, API에서 받아온 데이터가 있고, 사용자가 아직 수정하지 않은 경우에만 업데이트
      if (
        isEditMode &&
        sourceDocId &&
        formData.doc === sourceDocId &&
        executorListKey &&
        executorListKey !== currentExecutorListKey &&
        formData.executorList.length === 0 &&
        !hasExecutorListBeenManuallyEdited.current
      ) {
        setFormData(function (prev) {
          return {
            ...prev,
            executorList: convertedExecutorList,
          };
        });
      }
    },
    [
      isEditMode,
      sourceDocId,
      formData.doc,
      executorListKey,
      currentExecutorListKey,
      convertedExecutorList,
      formData.executorList.length,
    ],
  );

  // 검토자 정보 업데이트 (API에서 로드된 데이터만, 사용자가 수정하지 않은 경우에만)
  // 의존성을 단순화하고 데이터가 준비되었을 때 확실하게 업데이트
  useEffect(
    function () {
      if (isEditMode && convertedCheckEmpList && convertedCheckEmpList.length > 0) {
        setFormData(function (prev) {
          // 이미 데이터가 있거나 사용자가 수정한 경우(라고 가정할 수 있는 상황)에는 덮어쓰지 않도록 주의
          // 하지만 여기서는 초기 로딩 시점이므로 API 데이터로 덮어쓰는 것이 맞음
          // 단, 무한 루프 방지를 위해 내용 비교
          const currentList = prev.checkEmpList;
          const isSame =
            currentList.length === convertedCheckEmpList.length &&
            currentList.every(function (item, index) {
              return item.enumber === convertedCheckEmpList[index].enumber;
            });

          if (!isSame) {
            return {
              ...prev,
              checkEmpList: convertedCheckEmpList,
            };
          }
          return prev;
        });
      }
    },
    [isEditMode, convertedCheckEmpList],
  );

  // 편집 모드: 가이드라인 정보 로드 (여러 개 지원)
  // approvalLineGuidesData를 useMemo로 메모이제이션하여 의존성 배열 문제 해결
  const memoizedApprovalLineGuidesData = useMemo(
    function () {
      return approvalLineGuidesData;
    },
    [approvalLineGuidesData],
  );

  useEffect(
    function () {
      // 편집 모드이고 가이드라인 데이터가 로드되면 실행
      // formData.doc 체크를 제거하고 docId(URL 파라미터)와 데이터 유무로 판단
      if (
        isEditMode &&
        memoizedApprovalLineGuidesData &&
        memoizedApprovalLineGuidesData.length > 0
      ) {
        // 여러 개의 가이드라인을 approvalLineList로 변환
        const guides = memoizedApprovalLineGuidesData;
        const approvalLineList = guides.map(function (guide) {
          return {
            guideTitle: guide.guide_title || '',
            reviewerDept: guide.reviewer_dept || '',
            recipient: guide.recipient || '',
            approvalLineOptions: {
              teamLeader: guide.first_step_required === 1 || guide.first_step_required === true,
              departmentHead:
                guide.second_step_required === 1 || guide.second_step_required === true,
              agreement: guide.third_step_required === 1 || guide.third_step_required === true,
              agreementRequired:
                guide.agreement_required === 1 || guide.agreement_required === true,
              ceo: guide.ceo_required === 1 || guide.ceo_required === true,
            },
          };
        });

        setFormData(function (prev) {
          // 기존 데이터와 다를 때만 업데이트 (무한 루프 방지)
          if (JSON.stringify(prev.approvalLineList) !== JSON.stringify(approvalLineList)) {
            return {
              ...prev,
              approvalLineList: approvalLineList,
            };
          }
          return prev;
        });
      } else if (
        isEditMode &&
        (!memoizedApprovalLineGuidesData || memoizedApprovalLineGuidesData.length === 0)
      ) {
        // 가이드라인이 없는 경우 초기화 (데이터 로딩 완료 후)
        // 로딩 중이 아닐 때만 초기화
        // TODO: isLoading 상태를 정확히 체크할 필요 있음
      }
    },
    [isEditMode, memoizedApprovalLineGuidesData],
  );

  // 필드 변경 핸들러
  function handleFieldChange(field, value) {
    setHasUserEdited(true);
    setFormData(function (prev) {
      return {
        ...prev,
        [field]: value,
      };
    });
  }

  // 참조자/시행자/검토자 모달 열기
  function handleOpenRecipientModal(type) {
    setRecipientModalType(type);
    setRecipientModalOpen(true);
  }

  // 참조자/시행자/검토자 선택 완료
  function handleRecipientSelect(selectedList) {
    if (recipientModalType === 'sender') {
      hasSenderListBeenManuallyEdited.current = true;
      handleFieldChange('senderList', selectedList);
    } else if (recipientModalType === 'executor') {
      hasExecutorListBeenManuallyEdited.current = true;
      handleFieldChange('executorList', selectedList);
    } else if (recipientModalType === 'reviewer') {
      handleFieldChange('checkEmpList', selectedList);
    }
    setRecipientModalOpen(false);
    setRecipientModalType(null);
  }

  // 참조자/시행자/검토자 표시 텍스트
  function getRecipientDisplayText(list) {
    if (!list || list.length === 0) {
      return '없음';
    }
    return list.map((item) => item.name).join(', ');
  }

  // 결재라인 추가 핸들러
  function handleAddApprovalLine() {
    setHasUserEdited(true);
    setFormData(function (prev) {
      return {
        ...prev,
        approvalLineList: [
          ...prev.approvalLineList,
          {
            guideTitle: '',
            reviewerDept: '',
            recipient: '',
            approvalLineOptions: {
              teamLeader: false,
              departmentHead: false,
              agreement: false,
              agreementRequired: false,
              ceo: false,
            },
          },
        ],
      };
    });
  }

  // 결재라인 삭제 핸들러
  function handleRemoveApprovalLine(index) {
    setHasUserEdited(true);
    setFormData(function (prev) {
      if (prev.approvalLineList.length <= 1) {
        // 최소 1개는 유지
        return prev;
      }
      const newList = [...prev.approvalLineList];
      newList.splice(index, 1);
      return {
        ...prev,
        approvalLineList: newList,
      };
    });
  }

  // 결재라인 항목 필드 변경 핸들러
  function handleApprovalLineFieldChange(index, field, value) {
    setHasUserEdited(true);
    setFormData(function (prev) {
      const newList = [...prev.approvalLineList];
      newList[index] = {
        ...newList[index],
        [field]: value,
      };
      return {
        ...prev,
        approvalLineList: newList,
      };
    });
  }

  // 결재라인 옵션 변경 핸들러
  function handleApprovalLineOptionChange(index, option, value) {
    setHasUserEdited(true);
    setFormData(function (prev) {
      const newList = [...prev.approvalLineList];
      newList[index] = {
        ...newList[index],
        approvalLineOptions: {
          ...newList[index].approvalLineOptions,
          [option]: value,
        },
      };
      return {
        ...prev,
        approvalLineList: newList,
      };
    });
  }

  // 저장 핸들러
  async function handleSave() {
    // 기본 필수값 검사
    if (!formData.category || formData.category === '전체') {
      showAlert('문서 분류를 선택해주세요.');
      return;
    }

    if (!formData.title || formData.title.trim() === '') {
      showAlert('양식명을 입력해주세요.');
      return;
    }

    // 결재라인 정보 검사 (항상 표시되므로 항상 검사)
    // 각 결재라인 항목 검사
    for (let i = 0; i < formData.approvalLineList.length; i++) {
      const line = formData.approvalLineList[i];
      // 양식가이드 제목 필수
      if (!line.guideTitle || line.guideTitle.trim() === '') {
        showAlert(`결재라인 ${i + 1}의 양식가이드 제목은 필수값입니다.`);
        return;
      }

      // 결재라인 중 하나 이상 체크되어 있어야 함
      const hasApprovalLine =
        line.approvalLineOptions.teamLeader ||
        line.approvalLineOptions.departmentHead ||
        line.approvalLineOptions.agreement ||
        line.approvalLineOptions.agreementRequired ||
        line.approvalLineOptions.ceo;

      if (!hasApprovalLine) {
        showAlert(`결재라인 ${i + 1}에서 결재라인 중 하나 이상 선택해주세요.`);
        return;
      }
    }

    try {
      // API 요청 데이터 구성
      const requestData = {
        company: companyCode,
        title: formData.title,
        form: formData.form,
        form_data: formData.form_data,
        required_docs: formData.required_docs,
        category: formData.category, // 문서 분류
        senderList: formData.senderList.map(function (item) {
          return {
            code: item.enumber, // 백엔드에서 code 필드로 사용
            name: item.name || '',
            team: item.team || '',
            position: item.position || '',
          };
        }),
        executorList: formData.executorList.map(function (item) {
          return {
            code: item.enumber, // 백엔드에서 code 필드로 사용
            name: item.name || '',
            team: item.team || '',
            position: item.position || '',
          };
        }),
        // 검토자 정보를 check1~check4로 변환하여 전송
        check1: formData.checkEmpList.length > 0 ? formData.checkEmpList[0].enumber || 0 : 0,
        check2: formData.checkEmpList.length > 1 ? formData.checkEmpList[1].enumber || 0 : 0,
        check3: formData.checkEmpList.length > 2 ? formData.checkEmpList[2].enumber || 0 : 0,
        check4: formData.checkEmpList.length > 3 ? formData.checkEmpList[3].enumber || 0 : 0,
        // 결재라인 정보 (여러 개 지원)
        approvalLineEnabled: true,
        // 여러 개의 결재라인 가이드 정보 전송
        approvalLineList: formData.approvalLineList.map(function (line) {
          return {
            guideTitle: line.guideTitle || '',
            reviewerDept: line.reviewerDept || '',
            firstStepRequired: line.approvalLineOptions?.teamLeader || false,
            secondStepRequired: line.approvalLineOptions?.departmentHead || false,
            thirdStepRequired: line.approvalLineOptions?.agreement || false,
            agreementRequired: line.approvalLineOptions?.agreementRequired || false,
            ceoRequired: line.approvalLineOptions?.ceo || false,
            recipient: line.recipient || '',
          };
        }),
      };

      if (isEditMode) {
        requestData.doc = formData.doc;
        await updateFormMutation.mutateAsync(requestData);
      } else {
        await insertFormMutation.mutateAsync(requestData);
      }
    } catch (error) {
      console.error('저장 중 오류 발생:', error);
      showAlert('저장 중 오류가 발생했습니다.');
    }
  }

  // 삭제 핸들러
  function handleDelete() {
    if (window.confirm('정말 삭제하시겠습니까?')) {
      deleteFormMutation.mutate({
        docId: formData.doc,
        companyCode,
      });
    }
  }

  // 목록으로 이동 (변경사항 있으면 확인 후 이동)
  function handleGoToList() {
    if (hasUserEdited) {
      showConfirm('작성 중인 내용이 있습니다. 저장하지 않고 이동하시겠습니까?', function () {
        clearUnsaved();
        closeConfirm();
        navigate('/admin/forms/document');
      });
    } else {
      navigate('/admin/forms/document');
    }
  }

  if (isEditMode && isLoadingFormData) {
    return (
      <AdminLayout>
        <div className={styles.loadingContainer}>로딩 중...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className={styles.horizontalScrollWrapper}>
        <div className={styles.pageContainer}>
          {/* 헤더 영역 */}
          <div className={styles.headerArea}>
            <Button type="button" className={styles.listButton} onClick={handleGoToList}>
              목록
            </Button>
            <div className={styles.headerBorder} />
          </div>

          {/* 스플릿 레이아웃 */}
          <SplitPageLayout
            leftWidth="40%"
            rightWidth="60%"
            height="1240px"
            className={styles.splitLayout}
            leftChildren={
              <div className={styles.leftPanelContent}>
                <div className={styles.formFields}>
                  {/* 문서 분류 */}
                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>문서 분류</label>
                    <select
                      className={styles.select}
                      value={formData.category}
                      onChange={function (e) {
                        handleFieldChange('category', e.target.value);
                      }}
                    >
                      <option value="">선택</option>
                      {categoryOptions
                        .filter(function (opt) {
                          return opt !== '전체';
                        })
                        .map(function (opt) {
                          return (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          );
                        })}
                    </select>
                  </div>

                  {/* 양식명 */}
                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>양식명</label>
                    <input
                      type="text"
                      className={styles.input}
                      value={formData.title}
                      maxLength={200}
                      onChange={function (e) {
                        handleFieldChange('title', e.target.value);
                      }}
                      placeholder="양식명을 입력하세요"
                    />
                  </div>

                  {/* 양식 설명 */}
                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>양식 설명</label>
                    <input
                      type="text"
                      className={styles.input}
                      value={formData.form}
                      maxLength={200}
                      onChange={function (e) {
                        handleFieldChange('form', e.target.value);
                      }}
                      placeholder="양식 설명을 입력하세요"
                    />
                  </div>

                  {/* 필요 서류 (엔터 입력 가능, 3줄까지 표시 후 스크롤) */}
                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>필요 서류</label>
                    <textarea
                      className={styles.requiredDocsTextarea}
                      value={formData.required_docs}
                      onChange={function (e) {
                        handleFieldChange('required_docs', e.target.value);
                      }}
                      placeholder="필요 서류를 입력하세요"
                      rows={3}
                    />
                  </div>

                  {/* 참조자 */}
                  <div className={styles.fieldGroup}>
                    <div className={styles.recipientHeader}>
                      <label className={styles.fieldLabel}>참조자</label>
                      <Button
                        type="button"
                        className={styles.addButton}
                        onClick={function () {
                          handleOpenRecipientModal('sender');
                        }}
                      >
                        추가
                      </Button>
                    </div>
                    <input
                      type="text"
                      className={styles.input}
                      value={getRecipientDisplayText(formData.senderList)}
                      readOnly
                    />
                  </div>

                  {/* 시행자 */}
                  <div className={styles.fieldGroup}>
                    <div className={styles.recipientHeader}>
                      <label className={styles.fieldLabel}>시행자</label>
                      <Button
                        type="button"
                        className={styles.addButton}
                        onClick={function () {
                          handleOpenRecipientModal('executor');
                        }}
                      >
                        추가
                      </Button>
                    </div>
                    <input
                      type="text"
                      className={styles.input}
                      value={getRecipientDisplayText(formData.executorList)}
                      readOnly
                    />
                  </div>

                  {/* 검토자 */}
                  <div className={styles.fieldGroup}>
                    <div className={styles.recipientHeader}>
                      <label className={styles.fieldLabel}>검토자</label>
                      <Button
                        type="button"
                        className={styles.addButton}
                        onClick={function () {
                          handleOpenRecipientModal('reviewer');
                        }}
                      >
                        추가
                      </Button>
                    </div>
                    <input
                      type="text"
                      className={styles.input}
                      value={getRecipientDisplayText(formData.checkEmpList)}
                      readOnly
                    />
                  </div>

                  {/* 결재라인 정보 */}
                  <div className={styles.approvalLineSection}>
                    <div className={styles.approvalLineSectionHeader}>
                      <h3 className={styles.sectionHeader}>결재라인 안내</h3>
                    </div>

                    {formData.approvalLineList.map(function (line, index) {
                      return (
                        <div key={index}>
                          <div className={styles.approvalLineItem}>
                            {/* 1열: 양식 가이드 제목 */}
                            <div className={styles.fieldGroup}>
                              <label className={styles.fieldLabel}>양식 구분명</label>
                              <input
                                type="text"
                                className={styles.input}
                                value={line.guideTitle}
                                onChange={function (e) {
                                  handleApprovalLineFieldChange(
                                    index,
                                    'guideTitle',
                                    e.target.value,
                                  );
                                }}
                                placeholder="예) 품의서(10만원이상)"
                              />
                            </div>

                            {/* 2열: 검토 */}
                            <div className={styles.fieldGroup}>
                              <label className={styles.fieldLabel}>검토</label>
                              <input
                                type="text"
                                className={styles.input}
                                value={line.reviewerDept}
                                onChange={function (e) {
                                  handleApprovalLineFieldChange(
                                    index,
                                    'reviewerDept',
                                    e.target.value,
                                  );
                                }}
                                placeholder="예)전산파트, 회계팀"
                              />
                            </div>

                            {/* 3열: 팀장, 부문장(본부장), 합의(본부장), 대표이사 */}
                            <div className={styles.fieldGroup}>
                              <label className={styles.fieldLabel}>결재라인</label>
                              <div className={styles.checkboxRow}>
                                <label className={styles.checkboxLabel}>
                                  <input
                                    type="checkbox"
                                    checked={line.approvalLineOptions?.teamLeader || false}
                                    onChange={function (e) {
                                      handleApprovalLineOptionChange(
                                        index,
                                        'teamLeader',
                                        e.target.checked,
                                      );
                                    }}
                                  />
                                  1차
                                </label>
                                <label className={styles.checkboxLabel}>
                                  <input
                                    type="checkbox"
                                    checked={line.approvalLineOptions?.departmentHead || false}
                                    onChange={function (e) {
                                      handleApprovalLineOptionChange(
                                        index,
                                        'departmentHead',
                                        e.target.checked,
                                      );
                                    }}
                                  />
                                  2차
                                </label>
                                <label className={styles.checkboxLabel}>
                                  <input
                                    type="checkbox"
                                    checked={line.approvalLineOptions?.agreement || false}
                                    onChange={function (e) {
                                      handleApprovalLineOptionChange(
                                        index,
                                        'agreement',
                                        e.target.checked,
                                      );
                                    }}
                                  />
                                  3차
                                </label>
                                <label className={styles.checkboxLabel}>
                                  <input
                                    type="checkbox"
                                    checked={line.approvalLineOptions?.agreementRequired || false}
                                    onChange={function (e) {
                                      handleApprovalLineOptionChange(
                                        index,
                                        'agreementRequired',
                                        e.target.checked,
                                      );
                                    }}
                                  />
                                  합의(경영기획본부장)
                                </label>
                                <label className={styles.checkboxLabel}>
                                  <input
                                    type="checkbox"
                                    checked={line.approvalLineOptions?.ceo || false}
                                    onChange={function (e) {
                                      handleApprovalLineOptionChange(
                                        index,
                                        'ceo',
                                        e.target.checked,
                                      );
                                    }}
                                  />
                                  대표이사
                                </label>
                              </div>
                            </div>

                            {/* 4열: 수신자 (텍스트박스) */}
                            <div className={styles.fieldGroup}>
                              <label className={styles.fieldLabel}>참조</label>
                              <input
                                type="text"
                                className={styles.input}
                                value={line.recipient}
                                onChange={function (e) {
                                  handleApprovalLineFieldChange(index, 'recipient', e.target.value);
                                }}
                                placeholder="예) 업무 유관자, 회계팀"
                              />
                            </div>
                          </div>

                          {/* 버튼 영역 (각 항목 아래) */}
                          <div className={styles.approvalLineItemButtonArea}>
                            <div className={styles.approvalLineItemButtonGroup}>
                              {formData.approvalLineList.length > 1 && (
                                <Button
                                  type="button"
                                  className={styles.approvalLineDeleteButton}
                                  onClick={function () {
                                    handleRemoveApprovalLine(index);
                                  }}
                                >
                                  삭제
                                </Button>
                              )}
                              {index === formData.approvalLineList.length - 1 && (
                                <Button
                                  type="button"
                                  className={styles.approvalLineAddButton}
                                  onClick={handleAddApprovalLine}
                                >
                                  추가
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* 양식 삭제 버튼 (편집 모드만) */}
                {isEditMode && (
                  <div className={styles.deleteButtonWrapper}>
                    <Button
                      type="button"
                      className={styles.deleteButton}
                      onClick={handleDelete}
                      disabled={deleteFormMutation.isPending}
                    >
                      양식 삭제
                    </Button>
                  </div>
                )}
              </div>
            }
            rightChildren={
              <div className={styles.rightPanelContent}>
                <div className={styles.editorWrapper}>
                  <CKEditorWrapper
                    value={formData.form_data}
                    onChange={function (value) {
                      handleFieldChange('form_data', value);
                    }}
                    placeholder="내용을 입력하세요..."
                  />
                </div>
                <div className={styles.saveButtonWrapper}>
                  <Button
                    type="button"
                    className={styles.saveButton}
                    onClick={handleSave}
                    disabled={insertFormMutation.isPending || updateFormMutation.isPending}
                  >
                    저장
                  </Button>
                </div>
              </div>
            }
          />
        </div>
      </div>
      {/* 참조자/시행자/검토자 선택 모달 */}
      <FormRecipientModal
        isOpen={recipientModalOpen}
        onClose={function () {
          setRecipientModalOpen(false);
          setRecipientModalType(null);
        }}
        onSelect={handleRecipientSelect}
        title={
          recipientModalType === 'sender'
            ? '참조자 선택'
            : recipientModalType === 'executor'
              ? '시행자 선택'
              : '검토자 선택'
        }
        selectedRecipients={
          recipientModalType === 'sender'
            ? formData.senderList
            : recipientModalType === 'executor'
              ? formData.executorList
              : formData.checkEmpList
        }
      />
      {/* 목록 버튼 클릭 시 확인 */}
      <Confirm
        isOpen={confirmState.isOpen}
        message={confirmState.message}
        onConfirm={confirmState.onConfirm}
        onCancel={closeConfirm}
      />
      {/* LNB/로그아웃 클릭 시 저장 없이 이동 확인 */}
      <Confirm
        isOpen={Boolean(pendingLeave)}
        message="작성 중인 내용이 있습니다. 저장하지 않고 이동하시겠습니까?"
        onConfirm={confirmLeave}
        onCancel={cancelLeave}
      />
    </AdminLayout>
  );
}
